import {
  useEffect,
  useState,
  useRef,
  startTransition,
  useMemo,
  useCallback,
} from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import RavenLogo from "./RavenLogo/RavenLogo";
import { TopBarStyles } from "./TopBarStyles";
import { useAuth0 } from "../../contexts/AuthManager/Auth";
import {
  useIsAdmin,
  useGetPageFromPath,
  useRavenKiosk,
} from "../../utils/customHooks/genericHooks";
import { SERVER_NAMES } from "./Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { Location, useLocation, useNavigate } from "react-router";
import WifiBars from "./WifiBars";
import {
  MAGIC_STRING,
  getIsCypressRunning,
  getIsIe11,
} from "constants/constants";
import { setBackButtonLocation, getCustomersInToken } from "./topBarUtils";
import { UpOneLevelButton } from "./TopBarBackButton";
import { useLocalStorage } from "react-use";
import { AdminRightClickMenu } from "components/Dashboard/Tiles/CustomerTiles/AdminRightClickMenu";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { Fade, MenuItem, Tooltip } from "@mui/material";
import { isQuickCustomerSwitcherOpenAtom } from "components/QuickCustomerSwitcher";
import { useAtom } from "jotai";
import styled from "styled-components/macro";
import { lightGrey } from "constants/colors";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { TopBarBreadcrumbs } from "./TopBarBreadcrumbs";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useSelector } from "store/useSelector";
import { getIsTouchDevice } from "utils/getIsTouchDevice";
import { isFlightDebuggerOpenAtom } from "./Drawers/RightDrawer/FlightDebugger/debuggerUtils";

type TopBarProps = {
  onClickRightDrawerButton: () => void;
  onClickLeftDrawerButton: () => void;
};

const TopBar = ({
  onClickRightDrawerButton,
  onClickLeftDrawerButton,
}: TopBarProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathnameArray = pathname.split("/");
  const {
    numSlashes,
    isSettingsPage,
    isHelpPage,
    isHubletPage,
    isOperatorPage,
    isGroupTilesPage,
    isSchedulePage,
    isTrendsPage,
    isProcessTilesPage,
  } = useGetPageFromPath();
  const [lastLocationPathStored, setLastLocationPathStored] = useLocalStorage(
    "lastLocationPath",
    "/"
  );
  // customers with "all" as top level have one more slash at their top level
  const [
    {
      general: {
        autoRedirectIntoFirstGroup,
        redirectFromHubletViewPageToGridPage,
      },
    },
  ] = useFlightConfig();

  const numSlashesAtTopLevel = useMemo(
    () => (autoRedirectIntoFirstGroup ? 3 : 2),
    [autoRedirectIntoFirstGroup]
  );

  const lastLocation = useLastLocation();
  useEffect(() => {
    setBackButtonLocation(
      lastLocation,
      pathname,
      lastLocationPathStored ?? "",
      setLastLocationPathStored,
      autoRedirectIntoFirstGroup,
      ckey
    );
    // set back button location only whenever the url changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // longpress to redirect to top level (for internal use only)
  // * if re-using this long-press pattern, switch to https://hammerjs.github.io/
  const longPressTimer = useRef(null as number | null);
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        window.clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };
  }, []);

  const handleButtonPress = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      longPressTimer.current = window.setTimeout(() => {
        navigate("/");
      }, 1500);
    },
    [navigate]
  );

  const handleButtonRelease = useCallback(() => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const longPressProps = useMemo(
    () => ({
      onContextMenu: event => event.preventDefault(),
      onTouchStart: handleButtonPress,
      onMouseDown: handleButtonPress,
      onTouchEnd: handleButtonRelease,
      onMouseUp: handleButtonRelease,
      onMouseLeave: handleButtonRelease,
    }),
    [handleButtonPress, handleButtonRelease]
  );

  const doesCustomerRedirectToGridPage = redirectFromHubletViewPageToGridPage;
  const {
    wasLastPageSchedulesPage,
    wasLastPageReportsPage,
    wasLastPageOperatorPage,
  } = useMemo(() => {
    return {
      wasLastPageSchedulesPage: lastLocationPathStored?.includes("schedule"),
      wasLastPageReportsPage: lastLocationPathStored?.includes("reports"),
      wasLastPageOperatorPage: lastLocationPathStored?.includes("operator"),
    };
  }, [lastLocationPathStored]);
  const [workScheduleConfigList] = useWorkScheduleConfigJsonListAtom();

  const shouldGoBackNotUp = useMemo(
    () =>
      (wasLastPageSchedulesPage ||
        wasLastPageReportsPage ||
        isSettingsPage ||
        isHelpPage ||
        isOperatorPage || // go "up" from Hublet page if customer uses Grid pages (so they can navigate from Hublet page to other Dashboard pages)
        (isHubletPage &&
          !doesCustomerRedirectToGridPage &&
          !wasLastPageOperatorPage)) && // should go up if one workschedule && group tiles page
      !(isGroupTilesPage && workScheduleConfigList.length === 1),
    [
      isGroupTilesPage,
      isHubletPage,
      isOperatorPage,
      isSettingsPage,
      isHelpPage,
      wasLastPageSchedulesPage,
      wasLastPageReportsPage,
      doesCustomerRedirectToGridPage,
      workScheduleConfigList.length,
      wasLastPageOperatorPage,
    ]
  );

  // only show hamburger menu on fledge, localhost or grid page for now
  const isCustomerTilesPage = pathname === "/";
  const isLeftDrawerButtonVisible = !isCustomerTilesPage;
  const authContext = useAuth0();
  const user = authContext?.user;

  const isBackButtonVisible = useMemo(() => {
    const showWorkScheduleSwitcherDropdown =
      workScheduleConfigList.length > 1 && !isSchedulePage;
    const isTopLevelPage = isGroupTilesPage || isSchedulePage || isTrendsPage;

    const canGoBackToSchedulePage =
      showWorkScheduleSwitcherDropdown &&
      (isTopLevelPage || (isProcessTilesPage && autoRedirectIntoFirstGroup));
    const customersInToken = getCustomersInToken();
    const hasMoreThanOneCustomer =
      customersInToken.length > 1 || customersInToken.includes(MAGIC_STRING);
    const canGoBackToCustomerTilesPage =
      hasMoreThanOneCustomer && isTopLevelPage;

    return (
      numSlashes > numSlashesAtTopLevel ||
      pathnameArray.includes("settings") ||
      canGoBackToSchedulePage ||
      canGoBackToCustomerTilesPage ||
      pathnameArray.includes("help") ||
      pathnameArray.includes("snapshots")
    );
  }, [
    numSlashes,
    numSlashesAtTopLevel,
    pathnameArray,
    workScheduleConfigList.length,
    isSchedulePage,
    isTrendsPage,
    isGroupTilesPage,
    isProcessTilesPage,
    autoRedirectIntoFirstGroup,
  ]);

  const isIE = useMemo(() => getIsIe11(), []);
  const isAdminUser = useIsAdmin();

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const handleContextMenu = useCallback(
    event => {
      event.preventDefault();
      setContextMenu(
        contextMenu === null
          ? {
              mouseX: event.clientX + 2,
              mouseY: event.clientY - 6,
            }
          : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu

            // Other native context menus might behave different.
            // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
            null
      );
    },
    [contextMenu]
  );
  const handleClose = useCallback(() => {
    setContextMenu(null);
  }, []);
  const ckey = useCustomerKey();
  const [, setIsQuickCustomerSwitcherOpen] = useAtom(
    isQuickCustomerSwitcherOpenAtom
  );
  const [, setIsFlightDebuggerOpen] = useAtom(isFlightDebuggerOpenAtom);
  const isMac = navigator.platform.includes("Mac");

  const ravenKiosk = useRavenKiosk();

  const enableContextMenu = useMemo(
    () => isAdminUser && ckey && !getIsTouchDevice() && !ravenKiosk,
    [isAdminUser, ckey, ravenKiosk]
  );
  const doesRavenLogoOverlapBreadcrumbs = useDoesRavenLogoOverlapBreadcrumbs();

  const showLogo = !doesRavenLogoOverlapBreadcrumbs;

  return (
    <TopBarStyles $isIE={isIE} data-component-name={"Navbar"}>
      <Tooltip
        title={enableContextMenu ? `right-click for admin options` : ""}
        disableInteractive={true}
        PopperProps={{
          disablePortal: true,
        }}
      >
        <AppBar
          position="relative"
          className="root"
          elevation={0}
          onContextMenu={e => {
            if (enableContextMenu) {
              handleContextMenu(e);
            }
          }}
        >
          <Toolbar className="toolbar">
            <LeftDrawerButton
              onClickLeftDrawerButton={onClickLeftDrawerButton}
              isLeftDrawerButtonVisible={isLeftDrawerButtonVisible}
            />
            <UpOneLevelButton
              isBackButtonVisible={isBackButtonVisible}
              pathnameArray={pathnameArray}
              shouldGoBackNotUp={shouldGoBackNotUp}
              lastLocationPathStored={lastLocationPathStored ?? ""}
              longPressProps={longPressProps}
            />
            <TopBarBreadcrumbs isBackButtonVisible={isBackButtonVisible} />
            <FadeCypressFriendly
              in={showLogo}
              mountOnEnter={true}
              unmountOnExit={false} // keep logo in dom to be able to measure overlap and change showLogo accordingly
              timeout={{ enter: 500, exit: 250 }}
              style={{ transitionDelay: `${showLogo ? 500 : 0}ms` }}
            >
              <div className="logo-fade">
                <div className="logo" data-component-name={"Raven Home Button"}>
                  <RavenLogo />
                </div>
                <div className="spacer doNotPrint" />
              </div>
            </FadeCypressFriendly>

            <WifiBars />
            <IconButton
              className="btnRightDrawer"
              color="inherit"
              aria-label="RightDrawer"
              onClick={onClickRightDrawerButton}
              data-server-names={[SERVER_NAMES.auth0]}
              data-component-name={"Right Drawer Button"}
              size="large"
            >
              {user?.picture ? (
                <Avatar src={user.picture} className="avatar" />
              ) : (
                <AccountCircleIcon className="rightDrawerButton" />
              )}
            </IconButton>
          </Toolbar>
        </AppBar>
      </Tooltip>
      {isAdminUser && ckey && (
        <AdminRightClickMenu
          contextMenu={contextMenu}
          handleClose={handleClose}
          ckey={ckey}
          extraMenuItems={[
            <MenuItem
              key="switch-customer"
              onClick={() => {
                setIsQuickCustomerSwitcherOpen(true);
                handleClose();
              }}
            >
              {`Switch customers... `}
              <KeyCombo>
                <StyledKbd>Ctrl</StyledKbd>+<StyledKbd>K</StyledKbd>
              </KeyCombo>
            </MenuItem>,
            <MenuItem
              key="open-debugger"
              onClick={() => {
                startTransition(() => {
                  setIsFlightDebuggerOpen(true);
                  handleClose();
                });
              }}
            >
              {`Open debugger`}
              <KeyCombo>
                <StyledKbd>Ctrl</StyledKbd>+
                {isMac ? null : (
                  <>
                    <StyledKbd>Alt</StyledKbd>+
                  </>
                )}
                <StyledKbd>Shift</StyledKbd>+<StyledKbd>Q</StyledKbd>
              </KeyCombo>
            </MenuItem>,
          ]}
        />
      )}
    </TopBarStyles>
  );
};
const KeyCombo = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-left: 6px;
  font-size: 0.8em;
`;
const StyledKbd = styled.kbd`
  background: ${lightGrey};
  border-radius: 3px;
  padding: 4px 5px;
  font-size: 0.8em;
  line-height: 1;
  margin: 0 2px;
`;
export default TopBar;

function LeftDrawerButton({
  isLeftDrawerButtonVisible,
  onClickLeftDrawerButton,
}: {
  isLeftDrawerButtonVisible: boolean;
  onClickLeftDrawerButton: any;
}) {
  return (
    <StyledIconButton
      className={`btnLeftDrawer doNotPrint ${
        isLeftDrawerButtonVisible ? "" : "hidden"
      }`}
      data-component-name={"Left Drawer Button"}
      color="inherit"
      aria-label="LeftDrawer"
      onClick={onClickLeftDrawerButton}
      size="large"
    >
      <MenuIcon className="leftDrawerButton" />
    </StyledIconButton>
  );
}
function useLastLocation() {
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState<Location>(location);
  useEffect(() => {
    if (location.pathname && location.pathname !== lastLocation.pathname) {
      setLastLocation(location);
    }
  }, [lastLocation.pathname, location, location.pathname]);
  return lastLocation;
}

const StyledIconButton = styled(IconButton)`
  opacity: 1;
  transition: opacity 0.3s;
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;

/** Annoyingly, Fade here causes issues during e2e test -> instead, during e2e test, just show/hide instantly instead of fading */
function FadeCypressFriendly({ children, ...props }) {
  const isCypress = getIsCypressRunning();
  return isCypress ? (
    props.in ? (
      <>{children}</>
    ) : null
  ) : (
    <Fade {...props}>{children}</Fade>
  );
}

function useDoesRavenLogoOverlapBreadcrumbs() {
  const [doesRavenLogoOverlapBreadcrumbs, setdoesRavenLogoOverlapBreadcrumbs] =
    useState(false);
  // on mount, on window width resize, and on page change, check if the raven logo overlaps the breadcrumbs
  const windowSize = useWindowSize();
  const location = useLocation();
  const allGroups = useSelector(state => state.legacy.allGroups);

  useEffect(() => {
    // set a timer so the page can re-render a touch before checking the overlap
    const timer = window.setTimeout(() => {
      const ravenLogo = document.querySelector(
        `[data-component-name="Navbar"] [class*=RavenLogoStyles] .linkContainer`
      );
      const visibleBreadcrumbs = document.querySelectorAll(
        `[data-component-name="Navbar"] [class*=TopBarBreadcrumbs__BreadcrumbItem]:not(.hidden)`
      );
      const rightmostVisibleBreadcrumb =
        visibleBreadcrumbs[visibleBreadcrumbs.length - 1];
      const isOverlap =
        ravenLogo &&
        visibleBreadcrumbs &&
        rightmostVisibleBreadcrumb &&
        // overlaps if the logo's left side is < the breadcrumb's right side
        ravenLogo.getBoundingClientRect().left <
          rightmostVisibleBreadcrumb.getBoundingClientRect().right;
      setdoesRavenLogoOverlapBreadcrumbs(Boolean(isOverlap));
    }, 100);
    return () => {
      window.clearTimeout(timer);
    };
  }, [location.pathname, windowSize.innerWidth, allGroups]);

  return doesRavenLogoOverlapBreadcrumbs;
}
